import _ from 'underscore';
import { CampaignType } from '@bingads-webui-campaign/campaign-ui-common';

let biddingSchemeInLastBuilder;

export const BidColumnId = {
  CPC: 0,
  CPV: 1,
  CPM: 2,
  PercentCpc: 3,
  CommissionRate: 4,
  ManualCPA: 5,
};

const allBidColumnIds = _.values(BidColumnId);

export const getBiddingSchemeType = ({ currentCampaign }) => {
  if (currentCampaign && currentCampaign.BiddingScheme && currentCampaign.BiddingScheme.Type) {
    return currentCampaign.BiddingScheme.Type;
  }
  return null;
};

const getBidColumnIdByBidStrategyType = ({ bidStrategyType }) => {
  const bidStrategyAndColumnMap = {
    ManualCpc: BidColumnId.CPC,
    ManualCpa: BidColumnId.ManualCPA,
    ManualCpv: BidColumnId.CPV,
    ManualCpm: BidColumnId.CPM,
    EnhancedCpc: BidColumnId.CPC,
    MaxClicks: BidColumnId.CPC,
    MaxConversions: BidColumnId.CPC,
    TargetCpa: BidColumnId.CPC,
    TargetRoas: BidColumnId.CPC,
    MaxConversionValue: BidColumnId.CPC,
    TargetImpressionShare: BidColumnId.CPC,
    Commission: BidColumnId.CommissionRate,
    PercentCpc: BidColumnId.PercentCpc,
  };

  return bidStrategyAndColumnMap[bidStrategyType];
};

export const isBiddingSchemeUpdated = ({
  campaignId,
  currentCampaign,
}) => {
  if (!campaignId) { return false; }
  const biddingSchemeInCurrentBuilder = getBiddingSchemeType({ currentCampaign });
  if (biddingSchemeInCurrentBuilder !== biddingSchemeInLastBuilder) {
    biddingSchemeInLastBuilder = biddingSchemeInCurrentBuilder;
    return true;
  }
  return false;
};

export const getCurrentCampaignType = ({
  currentCampaign,
  campaignId,
  config,
}) => {
  if (campaignId) {
    return _.result(currentCampaign || config.get('CurrentCampaign'), 'CampaignType', null);
  }
  return null;
};

export const getWhitelist = ({
  currentCampaign,
  campaignId,
  IsVideoAdsForCampaignEnabled,
  IsManualCpmEnabled,
  IsHotelCampaignEnabled,
  config,
  getCurrentCampaignType: _getCurrentCampaignType,
  globalCampaignTypeFilter,
}) => {
  const currentCampaignType = _getCurrentCampaignType({
    currentCampaign,
    campaignId,
    config,
  });
  const isAccountOrAudience = !campaignId &&
    (
      globalCampaignTypeFilter === CampaignType.Audience ||
      globalCampaignTypeFilter === CampaignType.All
    );
  const isAccountOrHotel = !campaignId &&
    (
      globalCampaignTypeFilter === CampaignType.Hotel ||
      globalCampaignTypeFilter === CampaignType.All
    );

  return _.intersection([
    (currentCampaignType !== CampaignType.Shopping && currentCampaignType !== 'Shopping') && BidColumnId.CPC,
    (currentCampaignType === CampaignType.Audience || currentCampaignType === 'Audience' || isAccountOrAudience) && IsVideoAdsForCampaignEnabled && BidColumnId.CPV,
    (currentCampaignType === CampaignType.Audience || currentCampaignType === 'Audience' || isAccountOrAudience) && IsManualCpmEnabled && BidColumnId.CPM,
    (currentCampaignType === CampaignType.Hotel || currentCampaignType === 'Hotel' || isAccountOrHotel) && IsHotelCampaignEnabled && BidColumnId.PercentCpc,
    (currentCampaignType === CampaignType.Hotel || currentCampaignType === 'Hotel' || isAccountOrHotel) && IsHotelCampaignEnabled && BidColumnId.CommissionRate,
  ], allBidColumnIds);
};

/**
 * Processor getter.
 * @param {string[]} lockedColumns
 *    columns we should not add to preference proactively.
 *    if those columns can be found in the old column set, we won't remove them neither.
 * @returns {Function} - colums processer.
 */
export const columnProcessorBeforeSetRecordGetter = lockedColumns =>
  /**
   * For Adgroup grid in all level, we'll ignore any changes in lockedColumns.
   * Like SearchBid, CpvBid, etc.
   * We'll pass this as config to plugin and do the process right before every record saving.
   * @param {string[]} allBidColumns - columns user selected for save.
   * @param {string[]} oldColumns - columns saved in old record if existed.
   * @returns {Array} - colums processed.
   */
  (newColumns, oldColumns = []) => {
    if (oldColumns.length === 0) {
      return _.without(newColumns, ...lockedColumns);
    }
    const oldExistColumns = _.intersection(oldColumns, lockedColumns);
    const columnsToRemove = _.without(lockedColumns, ...oldExistColumns);
    return _.without(newColumns, ...columnsToRemove);
  };

export const filterInvisibleBidColumns = ({
  bidColumns,
  currentCampaign,
  campaignId,
  IsVideoAdsForCampaignEnabled,
  IsManualCpmEnabled,
  IsHotelCampaignEnabled,
  config,
  globalCampaignTypeFilter,
}) => {
  const whitelist = getWhitelist({
    currentCampaign,
    campaignId,
    IsVideoAdsForCampaignEnabled,
    IsManualCpmEnabled,
    IsHotelCampaignEnabled,
    config,
    getCurrentCampaignType,
    globalCampaignTypeFilter,
  });

  return _.intersection(bidColumns, whitelist);
};

export const getLockedBidColumnIds = ({
  campaignId,
  currentCampaign,
  IsVideoAdsForCampaignEnabled,
  IsManualCpmEnabled,
  IsHotelCampaignEnabled,
  config,
  globalCampaignTypeFilter,
}) => {
  let result = [];
  if (IsVideoAdsForCampaignEnabled || IsManualCpmEnabled || IsHotelCampaignEnabled) {
    if (!campaignId) { // account level
      result = result.concat(allBidColumnIds);
    } else {
      const bidStrategyType = getBiddingSchemeType({ currentCampaign });
      result.push(getBidColumnIdByBidStrategyType({
        bidStrategyType,
      }));
    }
  }

  const filteredResult = filterInvisibleBidColumns({
    bidColumns: result,
    campaignId,
    currentCampaign,
    IsVideoAdsForCampaignEnabled,
    IsManualCpmEnabled,
    IsHotelCampaignEnabled,
    config,
    globalCampaignTypeFilter,
  });

  return filteredResult;
};

export const getUnlockedBidColumnIds = ({
  campaignId,
  currentCampaign,
  IsVideoAdsForCampaignEnabled,
  IsManualCpmEnabled,
  IsHotelCampaignEnabled,
  config,
  globalCampaignTypeFilter,
}) => {
  const lockedBidColumns = getLockedBidColumnIds({
    campaignId,
    currentCampaign,
    IsVideoAdsForCampaignEnabled,
    IsManualCpmEnabled,
    IsHotelCampaignEnabled,
    config,
    globalCampaignTypeFilter,
  });

  const filteredResult = filterInvisibleBidColumns({
    bidColumns: _.without(allBidColumnIds, ...lockedBidColumns),
    campaignId,
    currentCampaign,
    IsVideoAdsForCampaignEnabled,
    IsManualCpmEnabled,
    IsHotelCampaignEnabled,
    config,
    globalCampaignTypeFilter,
  });

  return filteredResult;
};

export const getLockedBidColumns = ({
  campaignId,
  currentCampaign,
  allBidColumns,
  IsVideoAdsForCampaignEnabled,
  IsManualCpmEnabled,
  IsHotelCampaignEnabled,
  config,
  globalCampaignTypeFilter,
}) => {
  const ids = getLockedBidColumnIds({
    campaignId,
    currentCampaign,
    IsVideoAdsForCampaignEnabled,
    IsManualCpmEnabled,
    IsHotelCampaignEnabled,
    config,
    globalCampaignTypeFilter,
  });

  return _.map(ids, value => allBidColumns[value]);
};

export const getUnlockedBidColumns = ({
  campaignId,
  currentCampaign,
  allBidColumns,
  IsVideoAdsForCampaignEnabled,
  IsManualCpmEnabled,
  config,
  globalCampaignTypeFilter,
}) => {
  const ids = getUnlockedBidColumnIds({
    campaignId,
    currentCampaign,
    IsVideoAdsForCampaignEnabled,
    IsManualCpmEnabled,
    config,
    globalCampaignTypeFilter,
  });

  return _.map(ids, value => allBidColumns[value]);
};

export const getSelectedBidColumns = ({
  allBidColumns,
  IsVideoAdsForCampaignEnabled,
  IsManualCpmEnabled,
}) => {
  if (!IsVideoAdsForCampaignEnabled && !IsManualCpmEnabled) {
    return [allBidColumns[BidColumnId.CPC]];
  }
  return [];
};
